"use client";

import React, { useState, useEffect, useCallback } from "react";
import { X, Copy, Check, Globe, ExternalLink, Link, Info } from "lucide-react";
import { useLiveBlock, appDB } from "@/indexedDB";
import { useBlockActions } from "@/contexts/blockContext";
import { postWithAuth } from "@/lib/api-helpers";
import { toast } from "@/lib/toast";
import { BlockPreview } from "../../BlockPreviewCard";

interface PublishModalProps {
  open: boolean;
  onClose: () => void;
  noteId: string;
}

export default function PublishModal({ open, onClose, noteId }: PublishModalProps) {
  const [isPublishing, setIsPublishing] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchIndexing, setSearchIndexing] = useState(false);
  const [duplicateAsTemplate, setDuplicateAsTemplate] = useState(true);
  const { updateBlock, getBlock } = useBlockActions();

  const liveBlock = useLiveBlock(noteId);
  // Context-first: use in-memory context for instant reads, fall back to IDB
  const block = getBlock(noteId) || liveBlock;
  const blockValue = block?.value as any;
  const isPublished = blockValue?.publicly_published === true;
  const publicLinkId = blockValue?.public_link_id as string | undefined;
  const publicUrl = publicLinkId
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/public/note/${publicLinkId}`
    : "";

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const updateLocalState = useCallback(async (updates: Record<string, unknown>) => {
    // 1. Update IndexedDB (drives useLiveBlock re-render)
    const existing = await appDB.blocks.get(noteId);
    if (!existing) return;
    const newValue = { ...(existing.value as any), ...updates };
    await appDB.blocks.put({
      ...existing,
      value: newValue,
      localUpdatedAt: Date.now(),
    });
    // 2. Update block context (in-memory map used by other parts of the app)
    updateBlock(noteId, { value: newValue });
  }, [noteId, updateBlock]);

  const handlePublish = useCallback(async () => {
    setIsPublishing(true);
    setError(null);
    try {
      const data = await postWithAuth<any>("/api/note/publish", { blockId: noteId, isPublic: true });
      if (data.isError) {
        setError(data.message || "Failed to publish");
        return;
      }
      // Update IDB immediately so useLiveBlock reflects the new state
      await updateLocalState({
        publicly_published: true,
        public_link_id: data.public_link_id,
      });
    } catch {
      setError("Failed to publish. Please try again.");
    } finally {
      setIsPublishing(false);
    }
  }, [noteId, updateLocalState]);

  const handleUnpublish = useCallback(async () => {
    setIsPublishing(true);
    setError(null);
    try {
      const data = await postWithAuth<any>("/api/note/publish", { blockId: noteId, isPublic: false });
      if (data.isError) {
        setError(data.message || "Failed to unpublish");
        return;
      }
      // Update IDB immediately so useLiveBlock reflects the new state
      await updateLocalState({ publicly_published: false });
    } catch {
      setError("Failed to unpublish. Please try again.");
    } finally {
      setIsPublishing(false);
    }
  }, [noteId, updateLocalState]);

  const handleCopy = useCallback(async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  }, [publicUrl]);

  if (!open) return null;

  return (
    // Full width inside the mobile bottom sheet, fixed floating card on desktop.
    <div className="relative bg-modal rounded-lg shadow-2xl w-full md:w-[420px] overflow-hidden border border-component">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-component">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Publish to web
        </p>
      </div>

      {isPublished ? (
        /* ── Published state ── */
        <div className="px-4 py-4 space-y-4">
          {/* URL bar */}
          <div className="flex items-center gap-2 bg-gray-50 dark:bg-[#2a2a2a] rounded-lg px-3 py-2 border border-component">
            <Globe className="w-3.5 h-3.5 text-green-500 shrink-0" />
            <span className="text-xs text-gray-700 dark:text-gray-300 truncate flex-1 font-mono">
              {publicUrl}
            </span>
            <button
              type="button"
              title="Copy public link"
              onClick={handleCopy}
              className="shrink-0 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              {isCopied ? (
                <Check className="w-3.5 h-3.5 text-green-500" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
            </button>
          </div>

          {/* Options list */}
          <div className="divide-y divide-component rounded-lg border border-component overflow-hidden">
            <MenuRow label="Duplicate as template" right={<Toggle checked={duplicateAsTemplate} onChange={setDuplicateAsTemplate} />} />
            <MenuRow
              label="Embed this page"
              right={<Link className="w-4 h-4 text-gray-400" />}
              onClick={() => {
                const embed = `<iframe src="${publicUrl}" width="100%" height="600" frameborder="0" allowfullscreen></iframe>`;
                navigator.clipboard.writeText(embed);
              }}
            />
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}

          {/* Footer buttons */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleUnpublish}
              disabled={isPublishing}
              className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-component text-gray-700 dark:text-gray-300 hover-component  transition-colors disabled:opacity-50"
            >
              {isPublishing ? "Working…" : "Unpublish"}
            </button>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-blue-500 hover:bg-blue-700 text-white transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              View site
            </a>
          </div>
        </div>
      ) : (
        /* ── Unpublished state ── */
        <div className="px-5 py-4 space-y-4">
          {/* Browser-style live page preview (mirrors the site preview mockup). */}
          <div className="mx-auto w-[90%] overflow-hidden rounded-md border border-component shadow-md">
            {/* Chrome: window dots + the page's "tab" (icon + title) */}
            <div className="flex items-center gap-1.5 h-6 px-2.5 bg-background border-b border-component">
              <span className="h-1.5 w-1.5 rounded-full bg-gray-400/60" />
              <span className="h-1.5 w-1.5 rounded-full bg-gray-400/60" />
              <span className="h-1.5 w-1.5 rounded-full bg-gray-400/60" />
            </div>
            {/* Live page render with a soft bottom fade */}
            <div className="relative h-[200px] overflow-hidden bg-modal">
              <BlockPreview blockId={noteId} variant="card" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-b from-transparent to-background dark:to-[hsl(0deg_0%_12.55%)]" />
            </div>
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}

          <button
            type="button"
            onClick={handlePublish}
            disabled={isPublishing}
            className="w-full rounded-lg bg-blue-500 px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isPublishing ? "Publishing…" : "Publish"}
          </button>

          {/* Info note */}
          <div className="flex items-start gap-2">
            <Info className="mt-px h-4 w-4 shrink-0 text-sec" />
            <p className="text-xs leading-4 text-sec">
              When published to web, anyone with the link can view this page&apos;s content and see contributor names.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange?: (value: boolean) => void }) {
  return (
    <div
      role="switch"
      aria-checked={checked}
      tabIndex={0}
      onClick={() => onChange?.(!checked)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onChange?.(!checked);
        }
      }}
      className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors cursor-pointer ${
        checked ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"
      }`}
    >
      <span
        className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
          checked ? "translate-x-3.5" : "translate-x-0.5"
        }`}
      />
    </div>
  );
}

function MenuRow({
  label,
  right,
  onClick,
}: {
  label: string;
  right?: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover-component  transition-colors"
    >
      <span>{label}</span>
      {right && <span>{right}</span>}
    </button>
  );
}
